import { useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowForward from '@mui/icons-material/ArrowForward';
import PushPinOutlined from '@mui/icons-material/PushPinOutlined';
import Search from '@mui/icons-material/Search';
import { availableTabs, searchSections, type SectionContext, type SectionSearchResult } from './sections';

export type SearchAction = 'open' | 'pin';

interface CriteriaSearchProps {
  context: SectionContext;
  /** What a plain pick does. The icon on each result does the other one. */
  defaultAction: SearchAction;
  onPick: (sectionId: string, action: SearchAction) => void;
}

const ACTION_LABEL: Record<SearchAction, string> = {
  open: 'Go to tab',
  pin: 'Add to summary',
};

/**
 * Finds a criteria section by its title, a field label, or a common word for it, then either
 * jumps to it on its tab or pins it to the Summary.
 */
export function CriteriaSearch({ context, defaultAction, onPick }: CriteriaSearchProps) {
  const [input, setInput] = useState('');
  const tabTitles = new Map(availableTabs(context).map((tab) => [tab.key, tab.title]));
  const results = searchSections(input, context);
  const otherAction: SearchAction = defaultAction === 'open' ? 'pin' : 'open';

  const pick = (result: SectionSearchResult | null, action: SearchAction) => {
    if (!result) return;
    onPick(result.section.id, action);
    setInput('');
  };

  return (
    <Autocomplete<SectionSearchResult, false, false, false>
      options={results}
      value={null}
      inputValue={input}
      onInputChange={(_event, next, reason) => {
        if (reason !== 'reset') setInput(next);
      }}
      onChange={(_event, result) => pick(result, defaultAction)}
      // The results are already filtered and ranked by searchSections.
      filterOptions={(options) => options}
      getOptionLabel={(result) => result.section.title}
      isOptionEqualToValue={(a, b) => a.section.id === b.section.id}
      groupBy={(result) => tabTitles.get(result.section.tab) ?? ''}
      open={input.trim().length > 0}
      noOptionsText='No criteria match.'
      autoHighlight
      forcePopupIcon={false}
      blurOnSelect
      sx={{ width: 'min(18rem, 45vw)', flexShrink: 0 }}
      renderOption={({ key, ...props }, result) => (
        <Box component='li' key={key} {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant='body2' noWrap>
              {result.section.title}
            </Typography>
            {result.matched && (
              <Typography variant='caption' color='text.secondary' noWrap component='div'>
                {result.matched}
              </Typography>
            )}
          </Box>
          <Tooltip title={ACTION_LABEL[otherAction]}>
            <IconButton
              size='small'
              aria-label={`${ACTION_LABEL[otherAction]}: ${result.section.title}`}
              // Act on mousedown so the listbox doesn't close (and drop the click) first.
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                pick(result, otherAction);
              }}
              onClick={(event) => event.stopPropagation()}
            >
              {otherAction === 'pin' ? <PushPinOutlined fontSize='small' /> : <ArrowForward fontSize='small' />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder='Find a criterion…'
          slotProps={{
            ...params.slotProps,
            htmlInput: { ...params.slotProps.htmlInput, 'aria-label': 'Find a criterion' },
            input: {
              ...params.slotProps.input,
              startAdornment: (
                <InputAdornment position='start'>
                  <Search fontSize='small' />
                </InputAdornment>
              ),
            },
          }}
        />
      )}
    />
  );
}
